import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Stack,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Avatar,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import UserService from '../../services/user.service';
import RoleService from '../../services/role.service';
import { usePagination } from '../../hooks/usePagination';

// Memoized UserRow component to prevent unnecessary re-renders
const UserRow = React.memo(({ user, getInitials, getRoleColor, onEdit, onDelete }: {
  user: any;
  getInitials: (firstName?: string, lastName?: string, username?: string) => string;
  getRoleColor: (roleName: string) => any;
  onEdit: () => void;
  onDelete: () => void;
}) => {
  const formattedLastLogin = useMemo(() => {
    return user.lastLoginAt
      ? new Date(user.lastLoginAt).toLocaleDateString()
      : 'Never';
  }, [user.lastLoginAt]);

  const formattedCreatedAt = useMemo(() => {
    return user.createdAt
      ? new Date(user.createdAt).toLocaleDateString()
      : '-';
  }, [user.createdAt]);

  const displayName = useMemo(() => {
    return user.firstName && user.lastName
      ? `${user.firstName} ${user.lastName}`
      : user.username;
  }, [user.firstName, user.lastName, user.username]);

  return (
    <TableRow hover>
      <TableCell>
        <Stack direction="row" spacing={2} alignItems="center">
          <Avatar sx={{ width: 40, height: 40 }}>
            {getInitials(user.firstName, user.lastName, user.username)}
          </Avatar>
          <Box>
            <Typography variant="body1" fontWeight="medium">
              {displayName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              @{user.username}
            </Typography>
          </Box>
        </Stack>
      </TableCell>
      <TableCell>{user.email}</TableCell>
      <TableCell>
        <Chip
          label={user.role?.name || 'No Role'}
          size="small"
          color={getRoleColor(user.role?.type)}
        />
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1}>
          {user.confirmed ? (
            <Chip
              icon={<CheckCircleIcon />}
              label="Confirmed"
              size="small"
              color="success"
              variant="outlined"
            />
          ) : (
            <Chip
              label="Unconfirmed"
              size="small"
              color="warning"
              variant="outlined"
            />
          )}
          {user.blocked && (
            <Chip
              icon={<BlockIcon />}
              label="Blocked"
              size="small"
              color="error"
            />
          )}
        </Stack>
      </TableCell>
      <TableCell>{formattedLastLogin}</TableCell>
      <TableCell>{formattedCreatedAt}</TableCell>
      <TableCell align="right">
        <IconButton
          size="small"
          onClick={onEdit}
        >
          <EditIcon />
        </IconButton>
        <IconButton
          size="small"
          onClick={onDelete}
          color="error"
        >
          <DeleteIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
});

UserRow.displayName = 'UserRow';

const UserList: React.FC = () => {
  const navigate = useNavigate();
  const { pagination, setPagination, handlePageChange, handlePageSizeChange } = usePagination(25);
  const [users, setUsers] = useState<any[]>([]);
  const [roles, setRoles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [sortField, setSortField] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const fetchRoles = async () => {
    try {
      const response = await RoleService.getAll();
      setRoles(response.data?.roles || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = {
        'populate[role]': '*',
        'pagination[page]': pagination.page + 1, // Strapi uses 1-based indexing
        'pagination[pageSize]': pagination.pageSize,
        sort: `${sortField}:${sortOrder}`,
      };

      // Add search filter if search term exists
      if (searchTerm) {
        params['filters[$or][0][username][$containsi]'] = searchTerm;
        params['filters[$or][1][email][$containsi]'] = searchTerm;
        params['filters[$or][2][firstName][$containsi]'] = searchTerm;
        params['filters[$or][3][lastName][$containsi]'] = searchTerm;
      }

      // Add role filter if selected
      if (selectedRole) {
        params['filters[role][id][$eq]'] = selectedRole;
      }

      const response = await UserService.getAll(params);
      const usersData = response.data || [];
      const paginationMeta = response.data?.meta?.pagination || {};
      
      setUsers(usersData);
      setPagination({
        total: paginationMeta.total || 0,
        pageCount: paginationMeta.pageCount || 0,
      });
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, sortField, sortOrder, selectedRole, searchTerm, setPagination]);

  useEffect(() => {
    fetchRoles();
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDelete = useCallback(async (id: number) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await UserService.delete(id);
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
        alert('Failed to delete user. They may have associated data.');
      }
    }
  }, [fetchUsers]);

  const getInitials = useCallback((firstName?: string, lastName?: string, username?: string) => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    return username ? username.substring(0, 2).toUpperCase() : 'U';
  }, []);

  const getRoleColor = useCallback((roleName: string) => {
    const colors: any = {
      admin: 'error',
      manager: 'warning',
      accountant: 'info',
      employee: 'default',
      authenticated: 'default',
      public: 'default',
    };
    return colors[roleName?.toLowerCase()] || 'default';
  }, []);

  const handleSort = useCallback((field: string) => {
    setSortField((prevField) => {
      if (prevField === field) {
        setSortOrder((prevOrder) => prevOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setSortOrder('asc');
      }
      return field;
    });
    // Reset to first page when sort changes
    setPagination({ page: 0 });
  }, [setPagination]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    // Reset to first page when search changes
    setPagination({ page: 0 });
  }, [setPagination]);

  const handleRoleFilterChange = useCallback((value: string) => {
    setSelectedRole(value);
    // Reset to first page when filter changes
    setPagination({ page: 0 });
  }, [setPagination]);

  // Debounce search to avoid too many API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers();
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, fetchUsers]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Users</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/users/create')}
        >
          New User
        </Button>
      </Stack>

      <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
          size="small"
          sx={{ flex: 1, minWidth: '200px' }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Filter by Role</InputLabel>
          <Select
            value={selectedRole}
            label="Filter by Role"
            onChange={(e) => handleRoleFilterChange(e.target.value)}
          >
            <MenuItem value="">All Roles</MenuItem>
            {roles.map((role) => (
              <MenuItem key={role.id} value={String(role.id)}>
                {role.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleSort('username')}
            startIcon={sortField === 'username' && (sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
          >
            Username
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleSort('email')}
            startIcon={sortField === 'email' && (sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
          >
            Email
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleSort('createdAt')}
            startIcon={sortField === 'createdAt' && (sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />)}
          >
            Date Joined
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Joined</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => (
                <UserRow
                  key={user.id}
                  user={user}
                  getInitials={getInitials}
                  getRoleColor={getRoleColor}
                  onEdit={() => navigate(`/users/${user.id}`)}
                  onDelete={() => handleDelete(user.id)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && (
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={pagination.total}
          rowsPerPage={pagination.pageSize}
          page={pagination.page}
          onPageChange={(_, newPage) => handlePageChange(newPage)}
          onRowsPerPageChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
        />
      )}

      {!loading && users.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No users found
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default UserList;

